import { useTranslation } from 'react-i18next';
import { StickyZoneMenuId } from './StickyZoneMenuId';
import type { StickyZoneStyle } from '../../domain';
import { StickyZoneFontSize, StickyZoneShape, StickyZoneTitlePosition } from '../../domain';
import type { FontSize } from '@model-framework/text';
import { FontSizeMenu } from '@model-framework/text';
import { ColorMenu } from '@model-framework/color/ColorMenu';
import { TrashMenuButton } from '@model-framework/menu';
import { HyperLinkMenuButton } from '@model-framework/hyperlink/HyperLinkMenuButton';
import { StickyNodeGroupingMenuButton } from '@model-framework/menu/StickyNodeGroupingMenuButton';
import { ThemeColor } from '@view-model/models/common/color';
import type { TextAlign } from '@view-model/models/common/text-align';
import { TextAlignMenu } from '@view-model/models/common/text-align';
import { ZoneToViewMenuButton } from '@model-framework/menu/ZopeToViewMenuButton';
import { ElementDescriptionMenuButton } from '@model-framework/menu/ElementDescriptionMenuButton';
import { ZoneShapeMenu } from './ZoneShapeMenu';
import { ZoneTitlePositionMenu } from './ZoneTitlePositionMenu';

type Props = {
  menuId: StickyZoneMenuId;
  menuSize: number;
  currentStyle: StickyZoneStyle;
  isOpen: boolean;
  url: string | null;
  onMenuOpen(menu: StickyZoneMenuId): void;
  onMenuClose(): void;
  onFontSizeSelected(fontSize: FontSize): void;
  onThemeColorSelected(themeColor: ThemeColor): void;
  onTextAlignSelected(textAlign: TextAlign): void;
  onZoneShapeSelected(newShape: StickyZoneShape): void;
  onZoneTitlePositionSelected(titlePosition: StickyZoneTitlePosition): void;
  onSaveHyperLink(url: string | null): void;
  onToggleZoneDescription(): void;
  onDeleteZone(): void;
  onGroupSelectedZone(): void;
  onZoneToView(): void;
};

export const StickyZoneMenuContent: React.FC<Props> = ({
  menuId,
  menuSize,
  currentStyle,
  isOpen,
  url,
  onMenuOpen,
  onMenuClose,
  onFontSizeSelected,
  onThemeColorSelected,
  onTextAlignSelected,
  onZoneShapeSelected,
  onZoneTitlePositionSelected,
  onSaveHyperLink,
  onToggleZoneDescription,
  onDeleteZone,
  onGroupSelectedZone,
  onZoneToView,
}: Props) => {
  const { t } = useTranslation();
  return (
    <>
      {menuId === StickyZoneMenuId.FontSize && (
        <FontSizeMenu
          currentFontSize={currentStyle.fontSize}
          menuSize={menuSize}
          items={StickyZoneFontSize.values()}
          isOpen={isOpen}
          onMenuClicked={() => {
            isOpen ? onMenuClose() : onMenuOpen(menuId);
          }}
          onSelected={onFontSizeSelected}
          text={t('canvas.menu.textSize')}
        />
      )}
      {menuId === StickyZoneMenuId.ThemeColor && (
        <ColorMenu
          currentColorName={currentStyle.themeColor}
          menuSize={menuSize}
          items={ThemeColor.menuItems()}
          numColumns={4}
          isOpen={isOpen}
          text={t('canvas.menu.color')}
          onMenuClicked={() => {
            isOpen ? onMenuClose() : onMenuOpen(menuId);
          }}
          onSelected={onThemeColorSelected}
        />
      )}
      {menuId === StickyZoneMenuId.ZoneShape && (
        <ZoneShapeMenu
          currentShape={currentStyle.shape}
          menuSize={menuSize}
          items={StickyZoneShape.values()}
          isOpen={isOpen}
          text={t('canvas.menu.zone.shape')}
          onMenuClicked={() => {
            isOpen ? onMenuClose() : onMenuOpen(menuId);
          }}
          onSelected={onZoneShapeSelected}
        />
      )}
      {menuId === StickyZoneMenuId.ZoneTitlePosition && (
        <ZoneTitlePositionMenu
          currentTitlePosition={currentStyle.titlePosition}
          menuSize={menuSize}
          items={StickyZoneTitlePosition.values()}
          isOpen={isOpen}
          text={t('canvas.menu.zone.titlePosition')}
          onMenuClicked={() => {
            isOpen ? onMenuClose() : onMenuOpen(menuId);
          }}
          onSelected={onZoneTitlePositionSelected}
        />
      )}
      {menuId === StickyZoneMenuId.TextAlign && (
        <TextAlignMenu
          currentTextAlign={currentStyle.textAlign}
          menuSize={menuSize}
          isOpen={isOpen}
          onMenuClicked={() => {
            isOpen ? onMenuClose() : onMenuOpen(menuId);
          }}
          onSelected={onTextAlignSelected}
          text={t('canvas.menu.textAlign')}
        />
      )}
      {menuId === StickyZoneMenuId.HyperLink && (
        <HyperLinkMenuButton
          menuSize={menuSize}
          isOpen={isOpen}
          initialURL={url}
          onMenuClicked={() => {
            isOpen ? onMenuClose() : onMenuOpen(menuId);
          }}
          onClose={() => onMenuClose()}
          onSave={onSaveHyperLink}
          text={t('canvas.menu.hyperlink')}
        />
      )}
      {menuId === StickyZoneMenuId.ZoneDescription && (
        <ElementDescriptionMenuButton
          menuSize={menuSize}
          onClick={() => {
            // 他のメニューが開いていた場合閉じる
            onMenuClose();
            onToggleZoneDescription();
          }}
          text={t('canvas.menu.note')}
        />
      )}
      {menuId === StickyZoneMenuId.Grouping && (
        <StickyNodeGroupingMenuButton
          menuSize={menuSize}
          onClick={() => {
            // 他のメニューが開いていた場合閉じる
            onMenuClose();
            onGroupSelectedZone();
          }}
          text={t('canvas.menu.group')}
        />
      )}
      {menuId === StickyZoneMenuId.ConvertToView && (
        <ZoneToViewMenuButton
          menuSize={menuSize}
          onClick={() => {
            // 他のメニューが開いていた場合閉じる
            onMenuClose();
            onZoneToView();
          }}
          text={t('canvas.menu.zone.convertToView')}
        />
      )}
      {menuId === StickyZoneMenuId.DeleteZone && (
        <TrashMenuButton menuSize={menuSize} onClick={onDeleteZone} text={t('common.action.delete')} />
      )}
    </>
  );
};
